import type { Ref } from "vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import type { ProtectedUsersAction } from "@/constants/users.ts";

interface UseUsersPageOptions {
  fetchBulkActions: () => Promise<ProtectedUsersAction[]>;
  translationKeyPrefix?: string;
}

interface UseUsersPageReturn<TItem> {
  preview: Ref<TItem | null>;
  onPreviewUpdate: (item: TItem | null) => void;
  getErrorsList: (item: { suspended?: boolean; hasOpenTickets?: boolean }) => string[];
  availableBulkActions: Ref<{ name: ProtectedUsersAction; displayName: string }[]>;
  actionsLoading: Ref<boolean>;
  getBulkActions: () => Promise<void>;
}

export const useUsersPage = <TItem>({
  fetchBulkActions,
  translationKeyPrefix = "usersPage.actions",
}: UseUsersPageOptions): UseUsersPageReturn<TItem> => {
  const i18n = useI18n();
  const preview: Ref<TItem | null> = ref(null);

  const onPreviewUpdate = (item: TItem | null) => {
    preview.value = item ? { ...item } : null;
  };

  const getErrorsList = ({
    suspended = false,
    hasOpenTickets: unresolvedTickets = false,
  }: {
    suspended?: boolean;
    hasOpenTickets?: boolean;
  }): string[] => {
    const errors: { [key: string]: boolean } = {
      suspended,
      unresolvedTickets,
    };
    return Object.keys(errors).filter((key: string) => errors[key]);
  };

  const availableBulkActions = ref<{ name: ProtectedUsersAction; displayName: string }[]>([]);
  const actionsLoading = ref(false);

  const getBulkActions = async () => {
    availableBulkActions.value = [];
    actionsLoading.value = true;
    try {
      const actions = await fetchBulkActions();
      availableBulkActions.value = actions.map((name) => ({
        name,
        displayName: i18n.t(`${translationKeyPrefix}.${name}`) as string,
      }));
    } catch {
      availableBulkActions.value = [];
    } finally {
      actionsLoading.value = false;
    }
  };

  return {
    preview,
    onPreviewUpdate,
    getErrorsList,
    availableBulkActions,
    actionsLoading,
    getBulkActions,
  };
};
